import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { ActiveParkingSessionDTO, CalculateParkingPriceResultDTO, IGetHWControlledSessionInfoForLicensePlateDTO, ParkerRegistrationOffense, ParkingProcessZoneInfoDTO, PayTillNowResponseCode } from 'src/app/base/models/models.swagger.generated';
import { TranslateModule } from '@ngx-translate/core';
import { PriceUtils } from 'src/app/base/utils/price-utils';
import { PayTillNowService } from '../pay-till-now.service';
import { FastCeckinUserInfoFormType } from '../../fast-checkin/shared/fast-check-in-step-user-info/fast-checkin-user-info-form';
import { FormGroup } from '@angular/forms';
import { StringHelper } from 'src/app/base/shared/string-helper';

@Component({
  selector: 'app-pay-till-now-step-pay-now',
  templateUrl: './pay-till-now-step-pay-now.component.html',
  styleUrls: ['./pay-till-now-step-pay-now.component.scss'],
  imports: [TranslateModule ]
})
export class PayTillNowStepPayNowComponent implements OnInit, OnDestroy {
  @Input() public zoneInfo: ParkingProcessZoneInfoDTO | null = null;
  @Input() public validatingParkerId?: number = undefined;

  @Input() public userInfoForm: FormGroup<FastCeckinUserInfoFormType>;

  @Output() public submit = new EventEmitter<ActiveParkingSessionDTO>();
  @Output() public back = new EventEmitter<void>();

  public startTime = new Date();
  public customFormat = 'HH:mm';

  public showReadyToLeaveMessage = false;
  public readyToLeaveTimestamp = '';

  public get controlPlate(): string {
    return this.userInfoForm?.controls.controlPlate?.value;
  }

  public get priceWithCurrency(): string {
    if (this.parkingPriceResult?.parkingPriceDetails) {
      const normalPrice = this.parkingPriceResult.parkingPriceDetails.totalPriceWithVAT;
      const fallbackPrice = this.parkingPriceResult.fallbackParkingPriceDetails?.totalPriceWithVAT ?? 0;
      const parkingPrice = Math.max(normalPrice, fallbackPrice);
      return PriceUtils.getPriceWithCurrency(parkingPrice, this.parkingPriceResult.parkingPriceDetails.currency);
    }
    return '';
  }

  public get hasFallbackPrice(): boolean {
    if (!this.parkingPriceResult?.parkingPriceDetails) {
      return false;
    }
    const normalPrice = this.parkingPriceResult.parkingPriceDetails.totalPriceWithVAT;
    if (this.parkingPriceResult?.fallbackParkingPriceDetails && this.parkingPriceResult.fallbackParkingPriceDetails?.totalPriceWithVAT > normalPrice) {
      return true;
    }
    return false;
  }

  public get normalPriceWithCurrency(): string {
    if (this.parkingPriceResult?.parkingPriceDetails) {
      const normalPrice = this.parkingPriceResult.parkingPriceDetails.totalPriceWithVAT;
      return PriceUtils.getPriceWithCurrency(normalPrice, this.parkingPriceResult.parkingPriceDetails.currency);
    }
    return '';
  }

  public get hasOffense(): boolean {
    return this.parkingPriceResult?.offense != null && this.parkingPriceResult?.offense !== ParkerRegistrationOffense.NoOffense;
  }

  public get offenseType(): string {
    return ParkerRegistrationOffense[this.parkingPriceResult?.offense];
  }

  public totalParkingDuration: string = null;
  public alreadyPaidParkingDuration: string = null;

  public showError = false;
  public errorMessage = '';

  // check which ones are needed
  private timerSubscription: Subscription;
  private _isProcessing = false;
  private _activeSession: ActiveParkingSessionDTO | null = null;

  private get parkingPriceResult(): CalculateParkingPriceResultDTO | null {
    return this._activeSession?.pricingInformation?.parkingPrice ?? null;
  }

  constructor(private service: PayTillNowService, private spinnerService: SpinnerService) { }

  ngOnInit() {
    this.startSynchronizedTimer();
  }

  ngOnDestroy() {
    // Clean up timer subscription
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  private startSynchronizedTimer() {
    // Calculate milliseconds until next minute
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    if (msUntilNextMinute > 300) {
      // do not wait too long for the first update
      this.updatePriceInfo();
    }

    // Wait until the next minute, then start the recurring timer
    setTimeout(() => {
      // Trigger immediately when minute rolls over
      this.updatePriceInfo();

      // Then set up recurring timer for every minute
      this.timerSubscription = interval(60000).subscribe(() => {
        this.updatePriceInfo();
      });
    }, msUntilNextMinute);
  }

  private updatePriceInfo() {
    if (this._isProcessing) {
      return;
    }

    const data: IGetHWControlledSessionInfoForLicensePlateDTO = {
      zoneId: this.zoneInfo.id,
      operationalLicensePlate: this.userInfoForm.controls.controlPlate.value,
      fullImageRequested: false,
      licensePlateImageRequested: false,
      validatingParkerId: this.validatingParkerId
    };

    this.service.getHWControlledSessionInfoForLicensePlate(data).subscribe({
      next: result => {
        const sessionInfo = result?.session ?? null;
        this._activeSession = sessionInfo;
        if (!sessionInfo) {
          this.showError = true;
          this.errorMessage = 'i18n.domain.fast-checkin.errors.no-active-session-found';
          return;
        }

        if (!this.parkingPriceResult?.parkingPriceDetails) {
          this.spinnerService.hide();
          this.errorMessage = 'i18n.domain.fast-checkin.errors.startPaymentProcessFailed';
          this.showError = true;
          this._isProcessing = false;
          return;
        }

        if (this.parkingPriceResult.parkingPriceDetails.totalPriceWithVAT <= 0) {
          this.showReadyToLeaveMessage = true;
          this.readyToLeaveTimestamp = this._activeSession.pricingInformation.parkingPriceCalculationTime?.toFormat('HH:mm');
          this.timerSubscription?.unsubscribe(); // stop price updates since user is ready to leave
        }

        this.totalParkingDuration = StringHelper.transformToHoursMinutesRepresentation(this._activeSession.parkingDuration);
        this.alreadyPaidParkingDuration = null;
        if (this._activeSession.pricingInformation?.alreadyPaidParkingMinutes) {
          this.alreadyPaidParkingDuration = StringHelper.transformToHoursMinutesRepresentation(this._activeSession.pricingInformation?.alreadyPaidParkingMinutes ?? 0);
        }

        this.spinnerService.hide();
        this._isProcessing = false;
      },
      error: () => {
        this.spinnerService.hide();
        this.errorMessage = 'i18n.domain.fast-checkin.errors.startPaymentProcessFailed';
        this.showError = true;
        this._isProcessing = false;
      }
    });
  }

  public onPayNow() {
    if (this._isProcessing || !this._activeSession) {
      return;
    }
    this._isProcessing = true;
    this.spinnerService.show();
    this.service.startPayTillNowPayment(this._activeSession, this.userInfoForm.controls.email.value, this.validatingParkerId).subscribe({next: response => {
      this._isProcessing = false;
      this.spinnerService.hide();
      if (response?.responseCode == PayTillNowResponseCode.Success) {
        if (response.redirectionUrl) {
          window.location.href = response.redirectionUrl;
        } else {
          this.readyToLeaveTimestamp = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
          this.showReadyToLeaveMessage = true;
          this.timerSubscription?.unsubscribe(); // stop price updates since user is ready to leave
        }
      } else {
        this.errorMessage = 'i18n.domain.fast-checkin.errors.startPaymentProcessFailed';
        this.showError = true;
      }
    }, error: err => {
      this._isProcessing = false;
      this.errorMessage = 'i18n.domain.fast-checkin.errors.startPaymentProcessFailed';
      this.showError = true;
      this.spinnerService.hide();
    }});
  }

}
